<template>
    <!-- Heavy picker: whole card is one button + chevron (Contacts detail / Avalara). -->
    <UButton
        v-if="isSelectable && address && !skeleton"
        type="button"
        color="neutral"
        variant="ghost"
        block
        :ui="buttonUi"
        :class="ui.root"
        @click="emit('select')"
    >
        <span :class="ui.selectableInner">
            <span :class="ui.selectableBody">
                <span :class="[ui.head, verificationPill && ui.headSplit]">
                    <span :class="[ui.label, muted && ui.labelMuted]">{{
                        address.label || 'Address'
                    }}</span>
                    <span v-if="verificationPill" :class="verificationPillClass">{{
                        verificationPillLabel
                    }}</span>
                    <span v-else-if="address.is_default" :class="ui.defaultPill">Default</span>
                </span>
                <span :class="[ui.name, muted && ui.nameMuted]">{{ addresseeLine }}</span>
                <span :class="[ui.line, muted && ui.lineMuted]">{{ line1 }}</span>
                <span :class="[ui.line, muted && ui.lineMuted]">{{ line2 }}</span>
            </span>
            <span :class="ui.chevron" aria-hidden="true">
                <SharedAppIcon name="lucide:chevron-right" size="16" :stroke-width="2" />
            </span>
        </span>
    </UButton>

    <!-- Avalara verify: entered address shown as Not valid (display-only, no chevron). -->
    <article
        v-else-if="displayOnly && address && !skeleton"
        :class="ui.root"
        :aria-label="verificationPillLabel"
        aria-disabled="true"
        tabindex="-1"
    >
        <span :class="ui.selectableBody">
            <span :class="[ui.head, verificationPill && ui.headSplit]">
                <span :class="ui.label">{{ address.label || 'Address' }}</span>
                <span v-if="verificationPill" :class="verificationPillClass">{{
                    verificationPillLabel
                }}</span>
            </span>
            <span :class="ui.name">{{ addresseeLine }}</span>
            <span :class="ui.line">{{ line1 }}</span>
            <span :class="ui.line">{{ line2 }}</span>
        </span>
    </article>

    <!-- Send selected: MVP `.hm-contact-card` — name + lines, corner Edit, no manage chrome. -->
    <article v-else-if="isSummary" :class="ui.root" :aria-busy="skeleton || undefined">
        <template v-if="skeleton">
            <div :class="ui.summaryBody">
                <USkeleton class="h-5 w-40 max-w-full" />
                <USkeleton class="h-4 w-full max-w-[16rem]" />
                <USkeleton class="h-4 w-3/4 max-w-[12rem]" />
            </div>
            <USkeleton v-if="showEdit" class="h-4 w-12 shrink-0" />
        </template>
        <template v-else-if="address">
            <div :class="ui.summaryBody">
                <h4 :class="ui.name">{{ addresseeLine }}</h4>
                <div v-if="line1" :class="ui.line">{{ line1 }}</div>
                <div v-if="line2" :class="ui.line">{{ line2 }}</div>
            </div>
            <UButton
                v-if="showEdit"
                type="button"
                color="neutral"
                variant="link"
                :class="ui.summaryEdit"
                :aria-label="editLabel"
                @click.stop="emit('edit')"
            >
                <SharedAppIcon name="lucide:pen" size="14" :stroke-width="1.5" />
                <span>{{ editLabel }}</span>
            </UButton>
        </template>
    </article>

    <!-- Manage: label / Default / footer actions (Account + contact address lists). -->
    <article v-else :class="ui.root" :aria-busy="skeleton || undefined">
        <template v-if="skeleton">
            <div :class="ui.head">
                <USkeleton class="h-3 w-20" />
                <USkeleton v-if="showMakeDefault" class="h-7 w-24 rounded-full" />
            </div>
            <USkeleton class="mt-1 h-6 w-48 max-w-full" />
            <USkeleton class="h-4 w-full max-w-[16rem]" />
            <USkeleton class="h-4 w-3/4 max-w-[12rem]" />
            <div v-if="showFooter" :class="ui.actions">
                <USkeleton v-if="showEdit" class="h-4 w-10" />
                <USkeleton v-if="showDelete" class="ml-auto h-4 w-12" />
            </div>
        </template>
        <template v-else-if="address">
            <div :class="ui.head">
                <span :class="ui.label">{{ address.label || 'Address' }}</span>
                <span v-if="address.is_default && showDefault" :class="ui.defaultPill">
                    Default
                </span>
                <UButton
                    v-else-if="showMakeDefaultControl && !address.is_default"
                    color="neutral"
                    variant="ghost"
                    size="xs"
                    :class="ui.makeDefault"
                    @click="emit('make-default')"
                >
                    Make default
                </UButton>
            </div>
            <h4 :class="ui.name">{{ addresseeLine }}</h4>
            <div :class="ui.line">{{ line1 }}</div>
            <div :class="ui.line">{{ line2 }}</div>
            <div v-if="showFooter" :class="ui.actions">
                <UButton
                    v-if="showEdit"
                    color="neutral"
                    variant="link"
                    size="xs"
                    :class="ui.action"
                    @click="emit('edit')"
                >
                    {{ editLabel }}
                </UButton>
                <UButton
                    v-if="showDelete"
                    color="error"
                    variant="link"
                    size="xs"
                    :class="ui.actionDanger"
                    @click="emit('delete')"
                >
                    Delete
                </UButton>
            </div>
        </template>
    </article>
</template>

<script setup lang="ts">
/**
 * Saved-address card.
 *
 * Variants:
 * - `manage` (default) — `.pf-addr-card`: label, Default, footer Edit/Delete
 * - `summary` — Send selected `.hm-contact-card`: name + lines, corner Edit
 * - `selectable` prop — heavy picker row + chevron (`.cm-addr-picker-row`)
 *
 * Prefer `CranePickerAddressRow` for Choose Address drawer lists.
 */
import { mergeUiClassMap } from '@/utils/defuStrConcat'
import { addressSuggestionCopy } from '@/data/crane/addressSuggestion'
import type { DbAddressDisplayFields } from '~~/layers/base/utils/formatAddressLine'
import { formatAddressTwoLines } from '~~/layers/base/utils/formatAddressLine'

export type CraneAddressCardAddress = DbAddressDisplayFields & {
    label?: string | null
    prefix?: string | null
    first_name?: string | null
    last_name?: string | null
    suffix?: string | null
    is_default?: boolean
}

/** `manage` = Account/Contacts lists; `summary` = Send selected recipient/return. */
export type CraneAddressCardVariant = 'manage' | 'summary'

export type CraneAddressCardUi = {
    root?: string
    rootMuted?: string
    selectableInner?: string
    selectableBody?: string
    chevron?: string
    head?: string
    headSplit?: string
    label?: string
    labelMuted?: string
    defaultPill?: string
    verificationPillOutline?: string
    makeDefault?: string
    name?: string
    nameMuted?: string
    line?: string
    lineMuted?: string
    actions?: string
    action?: string
    actionDanger?: string
    summaryBody?: string
    summaryEdit?: string
}

const props = withDefaults(
    defineProps<{
        address?: CraneAddressCardAddress
        /** Three-line placeholder matching this card’s chrome (list loading). */
        skeleton?: boolean
        /**
         * `manage` — label / Default / footer actions.
         * `summary` — Send `.hm-contact-card` (name + lines, corner Edit).
         */
        variant?: CraneAddressCardVariant
        showEdit?: boolean
        showDelete?: boolean
        showMakeDefault?: boolean
        /**
         * Manage: mark the `is_default` address with the Default pill and gold card wash.
         * Return Addresses turns this off (the default follows the last used address).
         */
        showDefault?: boolean
        /** Visible + aria label for Edit controls (summary corner + manage footer). */
        editLabel?: string
        /**
         * When true, “Make default” is a button (pointer + keyboard). When false,
         * `is_default` is only a Default badge — no control to change it.
         */
        canClick?: boolean
        /**
         * Heavy picker: whole row is the control (chevron). Mutually exclusive with
         * Edit / Delete / Make default so those buttons are not nested.
         */
        selectable?: boolean
        /**
         * Avalara verify drawer: Suggested / Manual / Not valid pill in the card head.
         * `not-valid` with `displayOnly` — MVP `.cm-addr-picker-row--invalid` (50% opacity).
         */
        verificationPill?: 'suggested' | 'manual' | 'not-valid' | null
        /** Avalara verify: gold wash on the suggested pick row. */
        suggestedHighlight?: boolean
        /**
         * Selectable manual row in confirm mode: no per-line fade (row stays full strength).
         * Ignored when `displayOnly` — invalid row dims via root opacity instead.
         */
        muted?: boolean
        /** Avalara verify: non-interactive entered row; close drawer or Edit Address only. */
        displayOnly?: boolean
        /**
         * Summary only: gold border / neutral fill on hover (parent opens picker on
         * card click; Edit still stops propagation).
         */
        clickable?: boolean
        ui?: Partial<CraneAddressCardUi>
    }>(),
    {
        address: undefined,
        skeleton: false,
        variant: 'manage',
        showEdit: false,
        showDelete: false,
        showMakeDefault: false,
        showDefault: true,
        editLabel: 'Edit',
        canClick: false,
        selectable: false,
        verificationPill: null,
        suggestedHighlight: false,
        muted: false,
        displayOnly: false,
        clickable: false,
        ui: () => ({}),
    }
)

const emit = defineEmits<{
    edit: []
    delete: []
    'make-default': []
    select: []
}>()

const isSelectable = computed(() => props.selectable)
const isSummary = computed(
    () => !props.selectable && !props.displayOnly && props.variant === 'summary'
)

const verificationPillLabel = computed(() => {
    if (props.verificationPill === 'suggested') return addressSuggestionCopy.suggestedPill
    if (props.verificationPill === 'manual') return addressSuggestionCopy.manualPill
    if (props.verificationPill === 'not-valid') return addressSuggestionCopy.notValidPill
    return ''
})

const verificationPillClass = computed(() => {
    if (props.verificationPill === 'suggested') return ui.value.defaultPill
    if (props.verificationPill === 'manual' || props.verificationPill === 'not-valid') {
        return ui.value.verificationPillOutline
    }
    return ''
})

const showFooter = computed(
    () =>
        !props.selectable &&
        !props.displayOnly &&
        !isSummary.value &&
        (props.showEdit || props.showDelete)
)
const showMakeDefaultControl = computed(
    () =>
        !props.selectable &&
        !props.displayOnly &&
        !isSummary.value &&
        props.showMakeDefault &&
        props.canClick
)

const addresseeLine = computed(() => {
    const addr = props.address
    if (!addr) return ''
    const parts = [addr.prefix, addr.first_name, addr.last_name].filter(Boolean).join(' ').trim()
    return addr.suffix ? `${parts}, ${addr.suffix}` : parts
})

const lines = computed(() => (props.address ? formatAddressTwoLines(props.address) : []))
const line1 = computed(() => lines.value[0] ?? '')
const line2 = computed(() => lines.value[1] ?? '')

const defaultUi = computed((): Required<CraneAddressCardUi> => {
    const isDefault = Boolean(props.address?.is_default) && props.showDefault && !props.skeleton
    const selectable = props.selectable
    const displayOnly = props.displayOnly
    const summary = isSummary.value
    const suggestedHighlight = props.suggestedHighlight

    if (summary) {
        return {
            root: [
                'group relative flex items-start justify-between gap-3 rounded-md',
                'border border-(--border) bg-white px-[1.125rem] pt-[1.125rem] pb-5 text-left',
                'transition-[border-color,background-color] duration-200',
                props.clickable ? 'hover:border-(--gold-50) hover:bg-(--neutral-50)' : '',
            ]
                .filter(Boolean)
                .join(' '),
            rootMuted: '',
            selectableInner: '',
            selectableBody: '',
            chevron: '',
            head: '',
            headSplit: '',
            label: '',
            labelMuted: '',
            defaultPill: '',
            verificationPillOutline: '',
            makeDefault: '',
            name: 'mb-[0.1875rem] font-sans text-[1rem] font-medium leading-(--leading-headings) text-(--primary-700)',
            nameMuted: '',
            line: 'block font-sans text-md leading-(--leading-tight) text-(--neutral-700)',
            lineMuted: '',
            actions: '',
            action: '',
            actionDanger: '',
            summaryBody: 'flex min-w-0 flex-1 flex-col gap-[0.5rem]',
            summaryEdit: [
                'inline-flex h-auto shrink-0 items-center gap-1.5 px-1.5 py-1',
                'font-sans text-sm font-medium text-(--primary-700)',
                'hover:text-(--secondary-700)',
            ].join(' '),
        }
    }

    return {
        root: displayOnly
            ? [
                  'relative flex w-full flex-col gap-1.5 rounded border border-(--neutral-200)',
                  'bg-transparent px-[1.125rem] py-5 text-left',
                  'cursor-default opacity-50 pointer-events-none select-none',
              ].join(' ')
            : [
                  'group relative rounded border bg-white px-[1.125rem] py-5 text-left',
                  selectable
                      ? [
                            'h-auto w-full justify-start gap-0 font-normal shadow-none ring-0',
                            suggestedHighlight
                                ? 'border-(--gold-ring-strong) bg-[linear-gradient(165deg,var(--gold-wash)_0%,var(--white)_85%)] hover:border-(--gold) hover:bg-[linear-gradient(165deg,var(--gold-wash)_0%,var(--white)_85%)]'
                                : 'border-(--border) hover:border-(--gold) hover:bg-(--gold-tint)',
                        ].join(' ')
                      : 'flex flex-col gap-1.5',
                  !selectable && isDefault
                      ? 'border-(--gold-ring-strong) bg-[linear-gradient(165deg,var(--gold-tint)_0%,var(--white)_60%)]'
                      : !selectable
                        ? 'border-(--border)'
                        : '',
              ]
                  .filter(Boolean)
                  .join(' '),
        rootMuted: '',
        selectableInner: 'grid w-full min-w-0 grid-cols-[1fr_auto] items-center gap-3.5',
        selectableBody: 'flex min-w-0 flex-col gap-1.5',
        chevron: [
            'flex items-center justify-center text-(--neutral-400)',
            'transition-colors duration-200 group-hover:text-(--gold)',
        ].join(' '),
        head: 'mb-0.5 flex min-h-[30px] items-center justify-between gap-3',
        headSplit: 'w-full',
        label: [
            'text-[11px] font-semibold uppercase tracking-(--tracking-eyebrow)',
            'text-(--secondary-700)',
        ].join(' '),
        labelMuted: 'text-(--neutral-400)',
        defaultPill: [
            'inline-flex h-7 box-border items-center justify-center rounded-full bg-(--primary-500)',
            'px-2.5 text-sm font-semibold uppercase tracking-(--tracking-label) indent-(--tracking-label)',
            'text-white',
        ].join(' '),
        verificationPillOutline: [
            'inline-flex h-7 box-border items-center justify-center rounded-full border border-(--primary-500)',
            'bg-transparent px-2.5 text-sm font-semibold uppercase tracking-(--tracking-label)',
            'indent-(--tracking-label) text-(--primary-700)',
        ].join(' '),
        makeDefault: [
            'h-7 cursor-pointer rounded-full border border-(--neutral-300) bg-transparent px-4',
            'text-xs font-semibold uppercase tracking-(--tracking-label) text-(--secondary-700)',
            'hover:border-(--gold) hover:bg-(--gold-tint) hover:text-(--gold)',
        ].join(' '),
        name: 'm-0 font-serif text-xl font-normal text-(--primary-700)',
        nameMuted: 'text-(--neutral-400)',
        line: 'block text-md leading-[1.5] text-(--neutral-700)',
        lineMuted: 'text-(--neutral-400)',
        actions: ['mt-3 flex items-center gap-3.5 border-t border-(--fa-hairline) pt-4'].join(' '),
        action: [
            'cursor-pointer px-0 text-xs font-semibold uppercase tracking-(--tracking-label)',
            'text-(--secondary-700) hover:text-(--gold)',
        ].join(' '),
        actionDanger: [
            'ml-auto cursor-pointer px-0 text-xs font-semibold uppercase tracking-(--tracking-label)',
            'text-(--error-700) hover:text-(--error-800)',
        ].join(' '),
        summaryBody: '',
        summaryEdit: '',
    }
})

const ui = computed(
    () => mergeUiClassMap(defaultUi.value, props.ui ?? {}) as Required<CraneAddressCardUi>
)

/** Flatten UButton chrome so the picker grid owns padding and hover. */
const buttonUi = computed(() => ({
    base: 'relative h-auto min-h-0 gap-0 rounded px-0 py-0 font-normal',
}))
</script>
